import { ReactElement, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from 'router/routes';
import { useOrderSummaryQuery } from './hooks/useOrderSummaryQuery';
import { LoadingView } from './LoadingView/LoadingView';
import { ParsedPaymentUrlStatus, parsePaymentUrlStatus } from './utils/paymentUrlStatus';
import { OrderSummaryExpiredView } from './views/OrderSummaryExpiredView';
import { OrderSummaryFailureOrCancelledView } from './views/OrderSummaryFailureOrCancelledView';
import { OrderSummaryLoadErrorView } from './views/OrderSummaryLoadErrorView';
import { OrderSummaryPendingView } from './views/OrderSummaryPendingView';
import { OrderSummaryRefundedView } from './views/OrderSummaryRefundedView';
import { OrderSummarySuccessView } from './views/OrderSummarySuccessView';

const getQueryErrorViewForUrlStatus = (urlStatus: ParsedPaymentUrlStatus | null): ReactElement => {
  switch (urlStatus) {
    case 'EXPIRED':
      return <OrderSummaryExpiredView />;
    case 'CANCELLED':
      return <OrderSummaryFailureOrCancelledView paymentStatus='CANCELLED' />;
    case 'FAILURE':
      return <OrderSummaryFailureOrCancelledView paymentStatus='FAILURE' />;
    case 'PENDING':
      return <OrderSummaryPendingView />;
    case 'REFUNDED':
      return <OrderSummaryRefundedView />;
    case 'SUCCESS':
    default:
      return <OrderSummaryLoadErrorView />;
  }
};

export const OrderSummaryPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const search = location.search;
  const urlStatus = parsePaymentUrlStatus(new URLSearchParams(search).get('status'));
  const orderID = new URLSearchParams(search).get('CartID') ?? '';
  const paymentID = new URLSearchParams(search).get('paymentId') ?? '';
  const { data, isFetching, isError } = useOrderSummaryQuery(orderID ? orderID : paymentID);

  // useEffect(() => {
  //   //if (!ready) return;

  //   const pageName = getOrderSummaryPageViewName(location.pathname);
  //   const url = window.location.href;
  //   const signature = `${pageName}\0${location.pathname}\0${location.search}\0${location.key}`;

  //   const now = Date.now();
  //   if (
  //     lastOrderSummaryPageView &&
  //     lastOrderSummaryPageView.signature === signature &&
  //     now - lastOrderSummaryPageView.at < PAGE_VIEW_DEDUPE_MS
  //   ) {
  //     return;
  //   }
  //   lastOrderSummaryPageView = { signature, at: now };

  //   eventTracking.page.viewed({
  //     pageName,
  //     url,
  //   });
  // }, [ready, eventTracking, location.pathname, location.search, location.key]);

  useEffect(() => {
    if (!orderID && !paymentID) {
      navigate(ROUTES.home);
    }
  }, [orderID, navigate, paymentID]);

  useEffect(() => {
    const dataLayer: unknown[] = (window as any).dataLayer || [];
    const gtag = (...eventData: unknown[]) => {
      dataLayer.push(eventData);
    };
    if (!isFetching && !isError && data?.payment_status === 'SUCCESS') {
      gtag('event', 'successful_order', {
        event_category: 'order',
        event_label: 'order_confirmation',
        value: 1,
      });
    }
  }, [data?.payment_status, isFetching, isError]);

  // useEffect(() => {
  //   if (!ready || !data || isFetching || isError) return;
  //   const status = data.payment_status;
  //   if (
  //     status !== 'SUCCESS' &&
  //     status !== 'FAILURE' &&
  //     status !== 'CANCELLED' &&
  //     status !== 'EXPIRED'
  //   ) {
  //     return;
  //   }

  //   const key = `${data.id}-${status}`;
  //   if (orderOutcomeTrackedKeys.has(key)) return;
  //   orderOutcomeTrackedKeys.add(key);

  //   const orderId = Number(data.id) || 0;
  //   const eventId = data.item?.eventId ?? 0;
  //   const totalPrice = data.totalPrice ?? 0;

  //   // try {
  //   //   if (status === 'SUCCESS') {
  //   //     eventTracking.order.completed({ orderId, eventId, totalPrice });
  //   //     return;
  //   //   }

  //   //   if (status === 'CANCELLED' || status === 'EXPIRED') {
  //   //     eventTracking.order.cancelled({
  //   //       orderId,
  //   //       eventId,
  //   //       totalPrice,
  //   //       orderStatus: data.payment_status,
  //   //       errorCode: data.status || 'UNKNOWN',
  //   //     });
  //   //     return;
  //   //   }

  //   //   eventTracking.order.failed({
  //   //     orderId,
  //   //     eventId,
  //   //     totalPrice,
  //   //     orderStatus: data.payment_status,
  //   //     errorCode: data.status || 'UNKNOWN',
  //   //   });
  //   // } catch (error) {
  //   //   console.error('Failed to track order outcome:', error);
  //   // }
  // }, [ready, data, isFetching, isError, eventTracking]);

  if (isFetching) {
    return <LoadingView />;
  }

  if (!isError && data?.payment_status === 'SUCCESS') {
    return <OrderSummarySuccessView data={data} />;
  }

  if (!isError && data?.payment_status === 'EXPIRED') {
    return <OrderSummaryExpiredView />;
  }

  if (data?.payment_status === 'FAILURE' || data?.payment_status === 'CANCELLED') {
    return <OrderSummaryFailureOrCancelledView paymentStatus={data.payment_status} />;
  }

  if (data?.payment_status === 'PENDING') {
    return <OrderSummaryPendingView />;
  }

  if (data?.payment_status === 'REFUNDED') {
    return <OrderSummaryRefundedView />;
  }

  if (isError) {
    return getQueryErrorViewForUrlStatus(urlStatus);
  }

  return null;
};
